import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, PlayCircle, FileText, BookOpen, Headphones,
  Sparkles, Loader2, Check, ShieldAlert, LifeBuoy, Info,
} from "lucide-react";
import {
  v2GetState, v2GetLesson, v2UpdateProgress,
  v2PlaybackToken, v2SignedDownload,
} from "../../lib/api";

// -------- helpers --------
const AssetSlot = ({ label, state }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid={`asset-slot-${label.toLowerCase()}`}>
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/50">
        <Info size={14} />
      </div>
      <div className="flex-1">
        <div className="overline">{label} · MISSING</div>
        <div className="serif text-lg text-white/70 mt-0.5">Medien-Provider noch nicht konfiguriert</div>
        <p className="text-white/45 text-xs mt-1">{state?.detail || "Wird nach Provider-Freigabe bereitgestellt."}</p>
      </div>
    </div>
  </div>
);

const VideoPlayer = ({ assetId, onProgress }) => {
  const [tok, setTok] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!assetId) return;
    v2PlaybackToken(assetId).then(setTok).catch((e) => setErr(e?.response?.data?.detail || "kein Token"));
  }, [assetId]);
  if (err) return <div className="rounded-xl border border-red-400/20 bg-red-400/5 p-6 text-red-200/70">{err}</div>;
  if (!tok) return <div className="rounded-xl border border-white/10 p-6 text-white/50">Lade Video-Zugang…</div>;
  if (!tok.media_url) {
    return (
      <div className="rounded-2xl border border-[#D4AF37]/20 bg-white/[0.02] overflow-hidden" data-testid="video-slot-missing">
        <div className="aspect-video flex items-center justify-center bg-gradient-to-br from-[#060810] to-[#0D0B14]">
          <div className="text-center px-6">
            <div className="w-14 h-14 rounded-full border border-[#D4AF37]/40 mx-auto flex items-center justify-center text-[#D4AF37]">
              <PlayCircle size={24} />
            </div>
            <div className="mt-4 overline text-[#D4AF37]">Video · Provider PENDING</div>
            <p className="text-white/60 text-sm mt-2 max-w-md mx-auto">
              Diese Lektion ist strukturell freigeschaltet. Ein echter Medien-Provider ist
              in dieser Phase bewusst nicht angebunden. Die Anzeige bleibt sicher — keine
              öffentliche Video-URL wird erzeugt.
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl overflow-hidden border border-white/10 aspect-video" data-testid="video-slot-ready">
      <div className="w-full h-full flex items-center justify-center bg-black text-white/70 text-sm">
        (Synthetic playback token issued · TTL {tok.ttl_seconds}s — no real media)
      </div>
    </div>
  );
};

const WorkbookDownload = ({ assetId }) => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { setState(await v2SignedDownload(assetId)); }
    catch (e) { setState({ detail: e?.response?.data?.detail || "nicht verfügbar" }); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (assetId) load(); }, [assetId]);
  if (!assetId) return null;
  const ok = state && state.download_url;
  return (
    <div className="rounded-2xl border border-[#D4AF37]/20 bg-white/[0.02] p-5 flex items-center gap-4" data-testid="workbook-slot">
      <div className="w-11 h-11 rounded-full border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37]">
        <BookOpen size={18} />
      </div>
      <div className="flex-1">
        <div className="overline">Workbook</div>
        <div className="serif text-lg mt-0.5">{ok ? "Bereit zum Download" : "Storage-Provider PENDING"}</div>
        <p className="text-white/50 text-xs mt-1">
          {ok ? `Kurzlebiger Link · gültig ${state.ttl_seconds}s` : state?.detail || "Wird nach Freigabe bereitgestellt."}
        </p>
      </div>
      <button onClick={load} disabled={busy || !ok} className={ok ? "btn-primary text-sm" : "btn-ghost text-sm cursor-not-allowed opacity-60"}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : ok ? "Herunterladen" : "PENDING"}
      </button>
    </div>
  );
};

const CompletionBar = ({ lesson, onComplete, busy }) => {
  const rule = lesson.completion_rule;
  const done = lesson.progress?.status === "COMPLETED";
  if (done) {
    return <div className="flex items-center gap-2 text-[#D4AF37]" data-testid="lesson-completed"><Check size={16} /> Erledigt · integriert</div>;
  }
  if (rule === "SELF_ATTEST_READY") {
    return (
      <div className="flex flex-wrap gap-2" data-testid="self-attest-block">
        {["READY", "PAUSE", "SUPPORT_NEEDED"].map((v) => (
          <button key={v} onClick={() => onComplete({ self_attestation: v })}
            disabled={busy} className="btn-secondary text-sm">
            {v === "READY" ? "Ich bin bereit" : v === "PAUSE" ? "Pausiere kurz" : "Bitte um Unterstützung"}
          </button>
        ))}
      </div>
    );
  }
  if (rule === "VIDEO_90") {
    const pct = Math.round(lesson.progress?.video_progress_percent || 0);
    return (
      <div className="flex items-center gap-3" data-testid="video90-block">
        <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
          <div className="h-1 bg-[#D4AF37]" style={{ width: `${pct}%` }} />
        </div>
        <div className="text-white/50 text-xs w-14 text-right">{pct}%</div>
        <button onClick={() => onComplete({ video_percent_client: Math.min(100, pct + 20), last_position_seconds: 60 })}
          disabled={busy} className="btn-secondary text-sm">+20% signalisieren</button>
      </div>
    );
  }
  if (rule === "RESOURCE_AND_CONFIRM") {
    const gotResource = !!lesson.progress?.resource_accessed_at;
    return (
      <div className="flex flex-wrap items-center gap-2" data-testid="resource-confirm-block">
        <button onClick={() => onComplete({ resource_confirmed: true })}
          disabled={busy || gotResource} className="btn-ghost text-sm">
          {gotResource ? <><Check size={14} /> Ressource geöffnet</> : "Ressource bestätigen"}
        </button>
        <button onClick={() => onComplete({ action: "mark_complete" })}
          disabled={busy || !gotResource} className="btn-primary text-sm">
          <Check size={14} /> Bewusst abschliessen
        </button>
      </div>
    );
  }
  if (rule === "REVIEW_APPROVED") {
    return (
      <div className="text-sm text-white/60 flex items-center gap-2" data-testid="review-required-note">
        <ShieldAlert size={14} className="text-[#D4AF37]" />
        Diese Lektion wird nach Reviewer-Freigabe abgeschlossen. Deine Einreichung geht an einen Mentor-Reviewer.
      </div>
    );
  }
  return (
    <button onClick={() => onComplete({ action: "mark_complete" })}
      disabled={busy} className="btn-primary text-sm" data-testid="manual-confirm-btn">
      {busy ? <Loader2 className="animate-spin" size={14} /> : <><Check size={14} /> Als integriert markieren</>}
    </button>
  );
};

// -------- main --------
export default function PremiumPlayer() {
  const { courseSlug, lessonSlug } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const s = await v2GetState(courseSlug);
      setState(s);
      const l = await v2GetLesson(courseSlug, lessonSlug);
      setLesson(l);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Lektion nicht erreichbar");
    }
  };
  useEffect(() => { setErr(""); setLesson(null); load(); /* eslint-disable-next-line */ }, [courseSlug, lessonSlug]);

  const { prev, next } = useMemo(() => {
    if (!state || !lesson) return { prev: null, next: null };
    const unlocked = state.lessons.filter((l) => l.unlocked);
    const idx = unlocked.findIndex((l) => l.slug === lessonSlug);
    return {
      prev: idx > 0 ? unlocked[idx - 1] : null,
      next: idx >= 0 && idx < unlocked.length - 1 ? unlocked[idx + 1] : null,
    };
  }, [state, lesson, lessonSlug]);

  const onComplete = async (payload) => {
    setBusy(true);
    try {
      const p = await v2UpdateProgress(lesson.id, payload);
      setLesson((prev) => ({ ...prev, progress: {
        status: p.status,
        video_progress_percent: p.video_progress_percent,
        self_attestation: p.self_attestation,
        resource_accessed_at: p.resource_accessed_at,
        last_position_seconds: p.last_position_seconds,
      }}));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Aktualisierung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  if (err) return (
    <div className="pt-32 container-sat" data-testid="player-error">
      <Link to={`/academy/${courseSlug}`} className="btn-ghost text-sm"><ArrowLeft size={14} /> Zur Kursübersicht</Link>
      <div className="glass mt-6 rounded-2xl p-10 text-center">
        <div className="serif text-2xl mb-2">Lektion nicht erreichbar</div>
        <p className="text-white/60 text-sm">{err}</p>
      </div>
    </div>
  );
  if (!lesson || !state) return <div className="pt-32 text-center text-white/55">Lade Lektion…</div>;

  return (
    <div className="pt-24 min-h-screen bg-[#060810]" data-testid="premium-player">
      <div className="container-sat py-8 max-w-4xl">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs tracking-wider text-white/45 uppercase mb-6">
          <Link to="/academy/dashboard" className="hover:text-white/80">Dashboard</Link>
          <span>›</span>
          <Link to={`/academy/${courseSlug}`} className="hover:text-white/80" data-testid="breadcrumb-course">{state.course.title}</Link>
          <span>›</span>
          <span className="text-white/70">{lesson.title}</span>
        </div>

        <div className="glass rounded-3xl p-8 md:p-12">
          <div className="overline">{lesson.lesson_type} · {lesson.estimated_minutes} min</div>
          <h1 className="serif text-3xl md:text-5xl font-light mt-2 mb-8" data-testid="lesson-title">{lesson.title}</h1>

          {lesson.safety_note && (
            <div className="rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/[0.04] p-4 mb-6 flex gap-3" data-testid="safety-note">
              <ShieldAlert className="text-[#D4AF37] shrink-0" size={16} />
              <div className="text-white/75 text-sm">{lesson.safety_note}</div>
            </div>
          )}

          {/* Video slot */}
          {lesson.lesson_type === "VIDEO" && lesson.video_asset_id && (
            <div className="mb-6"><VideoPlayer assetId={lesson.video_asset_id} onProgress={() => {}} /></div>
          )}
          {lesson.lesson_type === "AUDIO" && !lesson.audio_asset_id && (
            <div className="mb-6"><AssetSlot label="Audio" state={{ detail: "Audio-Asset in Vorbereitung" }} /></div>
          )}

          {/* Body */}
          {lesson.body && (
            <div className="prose prose-invert max-w-none text-white/80 leading-relaxed text-lg mb-6" data-testid="lesson-body">
              {lesson.body.split("\n").map((p, i) => p ? <p key={i}>{p}</p> : <br key={i} />)}
            </div>
          )}

          {/* Workbook */}
          {lesson.workbook_asset_id && (
            <div className="mb-6"><WorkbookDownload assetId={lesson.workbook_asset_id} /></div>
          )}

          {/* Integration + Transfer + Support */}
          {(lesson.integration_prompt || lesson.transfer_48h) && (
            <div className="grid md:grid-cols-2 gap-4 mt-8 mb-6">
              {lesson.integration_prompt && (
                <div className="rounded-xl border border-white/10 p-5" data-testid="integration-prompt">
                  <div className="overline">Integrationsfrage</div>
                  <p className="serif text-lg mt-2 text-white/85">{lesson.integration_prompt}</p>
                </div>
              )}
              {lesson.transfer_48h && (
                <div className="rounded-xl border border-white/10 p-5" data-testid="transfer-48h">
                  <div className="overline">48-Stunden-Transfer</div>
                  <p className="serif text-lg mt-2 text-white/85">{lesson.transfer_48h}</p>
                </div>
              )}
            </div>
          )}

          {lesson.support_note && (
            <div className="text-white/55 text-xs flex items-start gap-2 mb-6" data-testid="support-note">
              <LifeBuoy size={12} className="mt-0.5 text-[#D4AF37]/70" />
              {lesson.support_note}
            </div>
          )}

          {/* Completion */}
          <div className="border-t border-white/10 pt-6 flex flex-wrap items-center gap-4 justify-between">
            <CompletionBar lesson={lesson} onComplete={onComplete} busy={busy} />
            <div className="flex gap-2 ml-auto">
              {prev && (
                <Link to={`/academy/${courseSlug}/learn-v2/${prev.slug}`} className="btn-ghost text-sm" data-testid="btn-prev">
                  <ArrowLeft size={14} /> Zurück
                </Link>
              )}
              {next && (
                <Link to={`/academy/${courseSlug}/learn-v2/${next.slug}`} className="btn-primary text-sm" data-testid="btn-next">
                  Weiter <ArrowRight size={14} />
                </Link>
              )}
              {!next && lesson.progress?.status === "COMPLETED" && (
                <Link to={`/academy/${courseSlug}`} className="btn-primary text-sm" data-testid="btn-back-to-course">
                  Zur Übersicht <ArrowRight size={14} />
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
